/**
 * @fileoverview World System V3 schema definitions for magic systems, natural laws,
 * social customs, technologies, and materials.
 *
 * This file contains:
 * - Canonical field definitions for world systems (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All system-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-event.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * System-specific field definitions.
 * Combined with shared fields, these define all attributes of a world system.
 */
const SYSTEM_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'rules',
    label: 'Rules & Mechanics',
    schemaDescription: "How the system works",
    initialStateDescription: "The fundamental rules and mechanics of how this system operates, including any formulas, processes, or principles that govern it.",
    journeyDescription: "Current understanding of the system's rules and any newly revealed mechanics or exceptions.",
  },
  {
    key: 'scope',
    label: 'Scope & Reach',
    schemaDescription: "What or who is affected",
    initialStateDescription: "What or who is affected by this system, including geographic, social, or metaphysical boundaries.",
    journeyDescription: "Current understanding of the system's scope and any changes to its reach or influence.",
  },
  {
    key: 'source',
    label: 'Source & Origin',
    schemaDescription: "Where the system comes from",
    initialStateDescription: "Where this system originates from — whether natural, divine, invented, or emergent — and how it came to exist.",
    journeyDescription: "Current understanding of the system's origin and any newly revealed history or creation myths.",
  },
  {
    key: 'cost',
    label: 'Cost & Requirements',
    schemaDescription: "What it takes to use or interact with",
    initialStateDescription: "What it costs or requires to use, harness, or interact with this system — energy, materials, skill, sacrifice, or other prerequisites.",
    journeyDescription: "Current understanding of costs and any newly revealed requirements or trade-offs.",
  },
  {
    key: 'limitations',
    label: 'Limitations & Exceptions',
    schemaDescription: "What the system cannot do or edge cases",
    initialStateDescription: "What this system cannot do, its edge cases, failure modes, or conditions under which it breaks down.",
    journeyDescription: "Current understanding of limitations and any newly discovered exceptions or loopholes.",
  },
  {
    key: 'effects',
    label: 'Effects & Consequences',
    schemaDescription: "Outcomes of interaction with the system",
    initialStateDescription: "The outcomes — intended and unintended — of interacting with this system, including side effects and long-term consequences.",
    journeyDescription: "Known effects and any newly observed or revealed consequences of the system's use.",
  },
  {
    key: 'enforcement',
    label: 'Enforcement & Violation',
    schemaDescription: "How rules are maintained and what happens when broken",
    initialStateDescription: "How the system's rules are maintained or enforced, and what happens when they are broken, circumvented, or violated.",
    journeyDescription: "Current understanding of enforcement mechanisms and any observed violations or punishments.",
  },
  {
    key: 'interaction',
    label: 'Interaction with Other Systems',
    schemaDescription: "How it relates to other world systems",
    initialStateDescription: "How this system interacts with, complements, or conflicts with other systems, rules, or forces in the world.",
    journeyDescription: "Current understanding of cross-system interactions and any newly revealed synergies or conflicts.",
  },
];

/**
 * Complete field definitions for world systems.
 * Combines shared fields with system-specific fields.
 */
export const WORLD_SYSTEM_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...SYSTEM_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world systems.
 * - magic-system: Supernatural rules and costs ("Magic costs life force", "Spells require spoken words")
 * - natural-law: Physical or metaphysical rules ("Gravity reverses at night", "Time flows differently in the Fade")
 * - social-custom: Cultural norms, taboos, traditions ("Nobles never touch iron", "The dead must be burned before sunset")
 * - technology: Technological systems and constraints ("FTL requires jump gates", "Neural interfaces cause memory bleed")
 * - material: Substances with special properties (Mithril, Dragonglass, Void-steel, Unobtainium)
 */
export const WorldSystemElementEnum = z.enum(['magic-system', 'natural-law', 'social-custom', 'technology', 'material']);
export type WorldSystemElement = z.infer<typeof WorldSystemElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world system attributes schema from the canonical field definitions.
 * All fields are optional for world systems.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_SYSTEM_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // System-specific fields
    rules: z.ZodOptional<z.ZodString>;
    scope: z.ZodOptional<z.ZodString>;
    source: z.ZodOptional<z.ZodString>;
    cost: z.ZodOptional<z.ZodString>;
    limitations: z.ZodOptional<z.ZodString>;
    effects: z.ZodOptional<z.ZodString>;
    enforcement: z.ZodOptional<z.ZodString>;
    interaction: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world system attributes, used by agents that generate or extract
 * system details. All attributes are optional.
 */
export const WorldSystemAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world system's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldSystemSnapshotV3Schema = WorldSystemAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World System entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldSystemV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world system."),
  name: z.string().describe("The name of the system."),
  type: z.literal('system').describe("Discriminator for world element type."),
  element: WorldSystemElementEnum.describe("The subtype of system (magic-system, natural-law, social-custom, technology, or material)."),
  initialDescription: z.string().optional().describe("Brief description of the system before detailed generation."),
  snapshots: z.array(WorldSystemSnapshotV3Schema).describe("Complete timeline of system state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this system."),
  mainImageId: z.string().optional().describe("Reference to the main image for this system."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world system idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldSystemIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the system."),
  element: WorldSystemElementEnum.describe("The proposed subtype (magic-system, natural-law, social-custom, technology, or material)."),
  initialDescription: z.string().describe("Brief description of the system concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world systems.
 * Used when only ID and name are needed.
 */
export const WorldSystemRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('system'),
});

/**
 * Basic context schema for world systems.
 * Used for high-level summaries in agent context.
 */
export const WorldSystemBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('system'),
  element: WorldSystemElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world systems.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldSystemContextV3Schema = WorldSystemV3Schema.extend({
  latestSnapshot: WorldSystemSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldSystemAttributes = z.infer<typeof WorldSystemAttributesV3Schema>;
export type WorldSystemSnapshotV3 = z.infer<typeof WorldSystemSnapshotV3Schema>;
export type WorldSystemV3 = z.infer<typeof WorldSystemV3Schema>;
export type WorldSystemIdeaV3 = z.infer<typeof WorldSystemIdeaV3Schema>;
export type WorldSystemRefV3 = z.infer<typeof WorldSystemRefV3Schema>;
export type WorldSystemBasicV3 = z.infer<typeof WorldSystemBasicV3Schema>;
export type WorldSystemContextV3 = z.infer<typeof WorldSystemContextV3Schema>;
